import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Plug, CheckCircle2, Clock, AlertTriangle, Loader2, Lock, ExternalLink, LifeBuoy, X } from 'lucide-react';

const PROVIDERS = ['Greenhouse', 'Lever', 'Workable', 'BambooHR', 'iCIMS', 'UKG', 'ADP Recruiting', 'Other', 'None'];

const STATUS_META = {
  not_requested: { c: 'bg-gray-100 text-gray-600', label: 'Not Requested', icon: Clock },
  request_submitted: { c: 'bg-amber-100 text-amber-700', label: 'Request Submitted', icon: Clock },
  under_review: { c: 'bg-amber-100 text-amber-700', label: 'Under Administrative Review', icon: Clock },
  info_required: { c: 'bg-amber-100 text-amber-700', label: 'Additional Information Required', icon: AlertTriangle },
  approved: { c: 'bg-amber-100 text-amber-700', label: 'Approved', icon: CheckCircle2 },
  awaiting_authorization: { c: 'bg-amber-100 text-amber-700', label: 'Awaiting Employer Authorization', icon: Clock },
  awaiting_credentials: { c: 'bg-amber-100 text-amber-700', label: 'Awaiting API Credentials', icon: Clock },
  configuring: { c: 'bg-amber-100 text-amber-700', label: 'Configuration in Progress', icon: Clock },
  testing: { c: 'bg-amber-100 text-amber-700', label: 'Testing', icon: Clock },
  connected: { c: 'bg-emerald-100 text-emerald-700', label: 'Connected', icon: CheckCircle2 },
  connection_error: { c: 'bg-red-100 text-red-700', label: 'Connection Error', icon: AlertTriangle },
  paused: { c: 'bg-red-100 text-red-700', label: 'Temporarily Paused', icon: AlertTriangle },
  disconnected: { c: 'bg-red-100 text-red-700', label: 'Disconnected', icon: X },
  declined: { c: 'bg-red-100 text-red-700', label: 'Request Declined', icon: X },
};

const emptyForm = {
  company_name: '', contact_name: '', contact_email: '', contact_phone: '', company_website: '',
  billing_contact_name: '', billing_contact_email: '', provider: '', other_provider_name: '',
  ats_admin_name: '', ats_admin_email: '', active_job_count: '', location_count: '',
  job_import_preference: 'all', custom_questions_required: false, resume_required: true,
  cover_letter_required: false, error_notification_email: '', desired_launch_date: '', employer_notes: '',
  authorization_accepted: false,
};

const EmployerTabATS = () => {
  const [data, setData] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mi, el, pr] = await Promise.all([
        axios.get(`${API_URL}/ats/my-integration`),
        axios.get(`${API_URL}/ats/eligibility`),
        axios.get(`${API_URL}/ats/pricing`),
      ]);
      setData(mi.data); setEligibility(el.data); setPricing(pr.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitRequest = async () => {
    if (!form.authorization_accepted) { toast.error('Please accept the integration authorization'); return; }
    if (!form.provider) { toast.error('Please select your ATS provider'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/ats/requests`, {
        ...form,
        active_job_count: form.active_job_count ? Number(form.active_job_count) : null,
        location_count: form.location_count ? Number(form.location_count) : null,
      });
      toast.success('Request submitted');
      setFormOpen(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your ATS integration? This disables direct submission for mapped jobs.')) return;
    try { await axios.post(`${API_URL}/ats/disconnect`); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed'); }
  };

  const requestSupport = async () => {
    try { await axios.post(`${API_URL}/ats/support`, { message: 'Employer requested ATS support from dashboard' }); toast.success('Support request sent'); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;

  const status = data?.status || 'not_requested';
  const meta = STATUS_META[status] || STATUS_META.not_requested;
  const StatusIcon = meta.icon;
  const integ = data?.integration;
  const req = data?.request;
  const hasActive = req && !['declined', 'disconnected'].includes(status);

  return (
    <div className="space-y-6" data-testid="employer-ats-tab">
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
              <Plug className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Direct ATS Application Delivery</h3>
              <p className="text-sm text-gray-500 max-w-xl">Keep your current hiring system. Connect Greenhouse, Lever, or another supported ATS and receive I NEED A SOCIAL WORKER applications directly inside your existing recruiting workflow.</p>
            </div>
          </div>
          <Badge className={`${meta.c} text-sm`}><StatusIcon className="w-4 h-4 mr-1" />{meta.label}</Badge>
        </div>
      </div>

      {/* Pricing */}
      {pricing && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <p className="text-sm text-gray-500">ATS Integration Add-On</p>
            <p className="text-2xl font-bold text-gray-900">${pricing.recurring_addon_monthly}<span className="text-sm font-normal text-gray-500">/month</span></p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <p className="text-sm text-gray-500">One-Time Setup & Configuration</p>
            <p className="text-2xl font-bold text-gray-900">${pricing.one_time_setup}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <p className="text-sm text-gray-500">Enterprise / Multi-location</p>
            <p className="text-lg font-semibold text-gray-900">Contact us</p>
          </div>
        </div>
      )}
      {pricing && <p className="text-xs text-gray-500">{pricing.note}</p>}

      {/* Integration detail / request */}
      {hasActive ? (
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <h4 className="font-medium text-gray-900 mb-4">Integration Details</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Detail label="ATS provider" value={req?.provider} />
            <Detail label="Connection status" value={meta.label} />
            <Detail label="Billing status" value={integ ? `Setup: ${integ.setup_fee_status} · Add-on: ${integ.recurring_addon_status}` : 'Pending approval'} />
            <Detail label="Date requested" value={req?.created_at?.slice(0, 10)} />
            <Detail label="Date approved" value={req?.approved_at?.slice(0, 10) || '—'} />
            <Detail label="Last successful sync" value={integ?.last_sync_at?.slice(0, 10) || '—'} />
            <Detail label="Last test application" value={integ?.last_test_at?.slice(0, 10) || '—'} />
            <Detail label="Jobs connected" value={data?.stats?.jobs_connected ?? 0} />
            <Detail label="Applications delivered" value={data?.stats?.applications_delivered ?? 0} />
            <Detail label="Submission errors" value={data?.stats?.submission_errors ?? 0} />
          </div>
          {status === 'info_required' && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Our team needs additional information. {req?.admin_notes}
            </div>
          )}
          {status === 'declined' && req?.decline_reason && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">Declined: {req.decline_reason}</div>
          )}
          <div className="flex gap-2 mt-5">
            <Button variant="outline" onClick={requestSupport} data-testid="ats-support-btn"><LifeBuoy className="w-4 h-4 mr-2" />Request Support</Button>
            {integ && <Button variant="outline" className="text-red-600 border-red-200" onClick={disconnect} data-testid="ats-disconnect-btn"><X className="w-4 h-4 mr-2" />Disconnect</Button>}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          {eligibility?.eligible ? (
            <>
              <p className="text-gray-600 mb-4">You're eligible to request a Direct ATS integration.</p>
              <Button onClick={() => { setForm({ ...emptyForm, company_name: eligibility.company_name || '', contact_email: eligibility.email || '' }); setFormOpen(true); }} className="bg-primary hover:bg-primary-hover text-white" data-testid="request-ats-btn">
                <Plug className="w-4 h-4 mr-2" />Request ATS Connection
              </Button>
            </>
          ) : (
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-700 font-medium">Not eligible yet</p>
                <p className="text-sm text-gray-500 mb-3">{eligibility?.reason}</p>
                <Link to="/employer/subscription"><Button variant="outline">View Membership Plans</Button></Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Request form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="font-serif">Request ATS Connection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <SectionTitle>Company Information</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Company name" v={form.company_name} on={(x) => setForm({ ...form, company_name: x })} testid="ats-company-name" />
              <Field label="Contact name" v={form.contact_name} on={(x) => setForm({ ...form, contact_name: x })} />
              <Field label="Contact email" v={form.contact_email} on={(x) => setForm({ ...form, contact_email: x })} />
              <Field label="Contact phone" v={form.contact_phone} on={(x) => setForm({ ...form, contact_phone: x })} />
              <Field label="Company website" v={form.company_website} on={(x) => setForm({ ...form, company_website: x })} />
              <Field label="Billing contact name" v={form.billing_contact_name} on={(x) => setForm({ ...form, billing_contact_name: x })} />
              <Field label="Billing contact email" v={form.billing_contact_email} on={(x) => setForm({ ...form, billing_contact_email: x })} />
            </div>

            <SectionTitle>ATS Information</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Which ATS do you use?</Label>
                <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                  <SelectTrigger data-testid="ats-provider-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p === 'None' ? 'We do not currently use an ATS' : p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.provider === 'Other' && <Field label="Other ATS name" v={form.other_provider_name} on={(x) => setForm({ ...form, other_provider_name: x })} />}
              <Field label="ATS administrator name" v={form.ats_admin_name} on={(x) => setForm({ ...form, ats_admin_name: x })} />
              <Field label="ATS administrator email" v={form.ats_admin_email} on={(x) => setForm({ ...form, ats_admin_email: x })} />
              <Field label="Approx. active job openings" type="number" v={form.active_job_count} on={(x) => setForm({ ...form, active_job_count: x })} />
              <Field label="Number of locations" type="number" v={form.location_count} on={(x) => setForm({ ...form, location_count: x })} />
              <Field label="Error notification email" v={form.error_notification_email} on={(x) => setForm({ ...form, error_notification_email: x })} />
              <Field label="Desired launch date" type="date" v={form.desired_launch_date} on={(x) => setForm({ ...form, desired_launch_date: x })} />
            </div>
            <div className="flex flex-wrap gap-4">
              <CheckRow label="Import all active jobs (uncheck for selected only)" v={form.job_import_preference === 'all'} on={(c) => setForm({ ...form, job_import_preference: c ? 'all' : 'selected' })} />
              <CheckRow label="Require resume attachments" v={form.resume_required} on={(c) => setForm({ ...form, resume_required: c })} />
              <CheckRow label="Require custom application questions" v={form.custom_questions_required} on={(c) => setForm({ ...form, custom_questions_required: c })} />
              <CheckRow label="Require cover letters" v={form.cover_letter_required} on={(c) => setForm({ ...form, cover_letter_required: c })} />
            </div>
            <div><Label className="text-xs">Additional notes</Label><Textarea rows={2} value={form.employer_notes} onChange={(e) => setForm({ ...form, employer_notes: e.target.value })} /></div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <Checkbox checked={form.authorization_accepted} onCheckedChange={(c) => setForm({ ...form, authorization_accepted: !!c })} data-testid="ats-authorization-checkbox" className="mt-0.5" />
                <span>I confirm that I am authorized to request this integration on behalf of my organization. I authorize I NEED A SOCIAL WORKER to review our ATS configuration and, after final approval, transmit authorized job and applicant information between our organization's ATS and the I NEED A SOCIAL WORKER platform.</span>
              </label>
              <p className="text-xs text-gray-400 mt-2">
                <Link to="/employer-terms" className="underline">Employer Terms</Link> · <Link to="/privacy" className="underline">Privacy Policy</Link> · <Link to="/ats-terms" className="underline">ATS Integration Terms</Link>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={submitRequest} disabled={submitting || !form.authorization_accepted} className="bg-primary hover:bg-primary-hover text-white" data-testid="ats-submit-request-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div><dt className="text-gray-500 text-xs">{label}</dt><dd className="font-medium text-gray-900">{value ?? '—'}</dd></div>
);
const SectionTitle = ({ children }) => <h4 className="font-medium text-gray-800 text-sm border-b pb-1">{children}</h4>;
const Field = ({ label, v, on, type = 'text', testid }) => (
  <div><Label className="text-xs">{label}</Label><Input type={type} value={v} onChange={(e) => on(e.target.value)} className="mt-1" data-testid={testid} /></div>
);
const CheckRow = ({ label, v, on }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><Checkbox checked={v} onCheckedChange={on} />{label}</label>
);

export default EmployerTabATS;
